const isNumber = (value: string | number) => !Number.isNaN(Number(value));
/**
 *
 * @param seconds - e.g. 1800
 * @returns - if seconds is 1800, returns 00:30 since 1800 seconds is 30 minutes
 */

const secondsToString = (seconds: number): string | undefined => {
  if (!isNumber(seconds)) {
    return undefined;
  }

  let minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  minutes %= 60;

  const hoursStr = hours.toString().padStart(2, '0');
  const minutesStr = minutes.toString().padStart(2, '0');

  const timeString = `${hoursStr}:${minutesStr}`;

  return timeString;
};

/**
 *
 * @param seconds - e.g. 126000
 * @returns - if seconds is 126000, returns 11:00 since 126000 seconds is 35 hours from 0, which lands on 11.
 * secondsToString would return 35:00 in this scenario.
 */

const secondsToTimeString = (seconds: number): string | undefined => {
  if (!isNumber(seconds)) {
    return undefined;
  }

  let minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  minutes %= 60;

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  const hoursStr = date.getHours().toString().padStart(2, '0');
  const minutesStr = date.getMinutes().toString().padStart(2, '0');

  const timeString = `${hoursStr}:${minutesStr}`;

  return timeString;
};

/**
 *
 * @param seconds - e.g. 1710198000
 * @returns - if seconds is 1710198000, returns 2024-03-12
 */

const secondsToDate = (seconds: number): string | undefined => {
  if (!isNumber(seconds)) {
    return undefined;
  }

  const date = new Date(seconds * 1000);

  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);

  const _year = date.getFullYear();
  const _month = (date.getMonth() + 1).toString().padStart(2, '0');
  const _day = date.getDate().toString().padStart(2, '0');
  return `${_year}-${_month}-${_day}`;
};

/**
 *
 * @param variant - one of number or decimal
 * @param value - the value to be converted. If value is string, it's converted, and if it's a number, it remains a number.
 * @returns - if variant is number, returns a number, if variant is decimal, returns a decimal otherwise returns an empty string.
 */

const parseNumber = (value: string | number, variant?: 'number' | 'decimal') => {
  const convertedValue = Number(value);

  if (value == null || value === '') return '';

  if (isNumber(value) && variant === 'decimal') {
    return convertedValue;
  }

  return Math.floor(convertedValue);
};

/**
 *
 * @param seconds - e.g. 3600, 4800, 900
 * @returns - formatted time string, e.g., "1h", "1h 20 min", "15 min"
 */
const formatTime = (seconds: number | string, minutesSuffix: string = 'min'): string | undefined => {
  if (!isNumber(seconds)) {
    return undefined;
  }
  const _seconds = Number(seconds);
  let minutes = Math.floor(_seconds / 60);
  const hours = Math.floor(minutes / 60);
  minutes %= 60;

  if (hours > 0) {
    if (minutes > 0) {
      return `${hours}h ${minutes.toString().padStart(2, '0')} ${minutesSuffix}`;
    }
    return hours === 1 ? `60 ${minutesSuffix}` : `${hours.toString()}h`;
  }

  return `${minutes.toString()} ${minutesSuffix}`;
};

const getHoursFromTime = (time: number) => {
  if (!time) return time;

  const date = new Date(time * 1000);
  const hoursInSeconds = date.getHours() * 3600;
  const minutesInSeconds = date.getMinutes() * 60;

  return hoursInSeconds + minutesInSeconds;
};


export {
  secondsToString,
  secondsToTimeString,
  secondsToDate,
  parseNumber,
  formatTime,
  getHoursFromTime,
};
